import {
  capturingMoves,
  estimateLead,
  colorName,
  formatVertex,
  groupsInAtari,
  ladderEscapes,
  lifeAndDeath,
  opponent,
  readBudget,
  type Color,
  type Game,
  type GroupLife,
  type Vertex,
} from '@baduk/engine';
import type { ConceptId } from './concepts.js';

/**
 * Live observations about the position in front of the player.
 *
 * These are the things a teacher sitting beside you would point at. They are
 * facts, never opinions about the best move — that is `advise`. Keeping them
 * separate matters: a learner can be warned that a group is in atari without
 * being told what to do about it, which is where the learning happens.
 *
 * Everything is said in terms of Black and White rather than you and your
 * opponent. A remark about the board is true whoever happens to be reading it,
 * and there is not always a "you" to address — watching two bots play, the
 * second person had nobody to refer to.
 *
 * Each note is a headline, one line of fact, and — separately — the reason it
 * matters. A teacher says "these two are short of liberties" every time it is
 * true, and explains what liberties are once. Splitting the two lets the screen
 * do the same, and it is why several groups in the same trouble now produce one
 * note rather than the same paragraph repeated with a different number in it.
 */

export type ObservationKind = 'danger' | 'opportunity' | 'caution' | 'info';

export interface Observation {
  readonly kind: ObservationKind;
  readonly headline: string;
  /** One line: what is true, and where. Always shown. */
  readonly detail: string;
  /** Why it matters. The teaching, shown on request rather than every turn. */
  readonly why?: string;
  /** Points to highlight on the board. */
  readonly highlight: readonly Vertex[];
  readonly concept?: ConceptId;
  /** Higher shows first. */
  readonly urgency: number;
}

export interface ObserveOptions {
  /** Suppress gentler notes once a player no longer needs them. */
  readonly stage?: 1 | 2 | 3;
}

/** `C7`, `C7 and F4`, `C7, F4 and H2` — a list a person would read aloud. */
function listPoints(size: number, points: readonly Vertex[]): string {
  const labels = points.map((v) => formatVertex(size, v));
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** What is true about the position right now, from `color`'s point of view. */
export function observe(game: Game, color: Color, options: ObserveOptions = {}): Observation[] {
  const stage = options.stage ?? 1;
  const notes: Observation[] = [];
  const position = game.position;
  const foe = opponent(color);
  const size = game.size;
  const mine = colorName(color);
  const theirs = colorName(foe);

  // --- Groups that are about to die ---------------------------------------
  // Kept one note per group: each has its own escape point, and whether running
  // works is different for each. This is the one place the repetition is real.
  for (const group of groupsInAtari(position, color)) {
    const point = group.liberties[0];
    const escapes = ladderEscapes(position, group);
    const them = plural(group.stones.length, 'it', 'them');
    notes.push({
      kind: 'danger',
      headline: `${mine}’s ${describeGroup(group.stones.length)} at ${formatVertex(size, group.stones[0])} is in atari`,
      detail: escapes
        ? `${formatVertex(size, point)} captures ${them}. Running still works.`
        : `${formatVertex(size, point)} captures ${them}, and running is a ladder that only loses more.`,
      why: escapes
        ? 'A group on its last liberty comes off the board the moment that point is filled.'
        : 'In a ladder every escape leaves the runner in atari again, all the way to the edge. Better to treat these as lost and take profit elsewhere.',
      highlight: [...group.stones, point],
      concept: escapes ? 'atari' : 'ladder',
      urgency: 100 + group.stones.length,
    });
  }

  // --- Free stones on offer ------------------------------------------------
  const captures = capturingMoves(position, color);
  if (captures.length > 0) {
    const total = captures.reduce((sum, c) => sum + c.stones, 0);
    notes.push({
      kind: 'opportunity',
      headline: `${mine} can capture ${total} stone${total === 1 ? '' : 's'}`,
      detail: `At ${listPoints(size, captures.map((c) => c.vertex))}.`,
      why: `Captured stones come off the board and count against ${theirs} at the end.`,
      highlight: captures.map((c) => c.vertex),
      concept: 'capture',
      urgency: 80 + total,
    });
  }

  // --- Groups getting short of breath --------------------------------------
  // Collected rather than reported one by one. Three groups in the same trouble
  // used to mean the same paragraph three times over, which is what made the
  // coach read like a machine rather than a person watching the board.
  if (stage <= 2) {
    const short: { stones: readonly Vertex[]; at: Vertex }[] = [];
    const seen = new Set<Vertex>();
    for (let v = 0; v < position.area; v++) {
      if (position.at(v) !== color || seen.has(v)) continue;
      const group = position.group(v);
      if (!group) continue;
      for (const s of group.stones) seen.add(s);
      if (group.liberties.length === 2 && group.stones.length >= 2) {
        short.push({ stones: group.stones, at: group.stones[0] });
      }
    }

    if (short.length > 0) {
      notes.push({
        kind: 'caution',
        headline:
          short.length === 1
            ? `${mine} has a group of ${short[0].stones.length} down to two liberties`
            : `${mine} has ${short.length} groups down to two liberties`,
        detail: `At ${listPoints(size, short.map((g) => g.at))}.`,
        why: `Two liberties is two moves from capture — close enough for ${theirs} to start a fight here. Adding a stone is often worth more than it looks.`,
        highlight: short.flatMap((g) => [...g.stones]),
        concept: 'liberties',
        urgency: 40,
      });
    }
  }

  // --- What the opponent is threatening ------------------------------------
  const theirCaptures = capturingMoves(position, foe);
  if (theirCaptures.length > 0) {
    const biggest = theirCaptures[0];
    notes.push({
      kind: 'danger',
      headline: `${theirs} can capture ${biggest.stones} stone${biggest.stones === 1 ? '' : 's'}`,
      detail: `Left alone, ${formatVertex(size, biggest.vertex)} takes ${plural(biggest.stones, 'it', 'them')}.`,
      why: 'Worth weighing against whatever else looks big — a move elsewhere has to be worth more than this.',
      highlight: [biggest.vertex],
      concept: 'atari',
      urgency: 90,
    });
  }

  // --- Whether groups can actually live ------------------------------------
  notes.push(...lifeNotes(game, color, stage));

  // --- Where the game stands ------------------------------------------------
  if (game.moveCount > size) {
    // estimateLead is signed for Black; flip it to this colour's perspective.
    const lead = estimateLead(position, game.rules.komi);
    const margin = color === 1 ? lead : -lead;
    const close = Math.abs(margin) < 3;
    notes.push({
      kind: 'info',
      headline: close
        ? 'The game is close'
        : `${margin > 0 ? mine : theirs} leads by roughly ${Math.abs(margin).toFixed(0)} points`,
      detail: 'A rough estimate from influence, not a count.',
      why: close
        ? 'Neither side has a clear lead yet, so there is nothing to protect and nothing to chase.'
        : margin > 0
          ? `${mine} is ahead, and the side that is ahead should simplify: trade stones, settle groups, avoid unnecessary fights.`
          : `${mine} is behind, and the side that is behind should complicate: start a fight where there are more friendly stones nearby, and avoid straightforward trades.`,
      highlight: [],
      concept: 'counting',
      urgency: 10,
    });
  }

  return notes.sort((a, b) => b.urgency - a.urgency);
}

function describeGroup(size: number): string {
  return size === 1 ? 'stone' : `group of ${size}`;
}

/**
 * Life and death — the one thing a beginner cannot work out from the rules, and
 * the reason a learner will pour ten stones into a group that died five moves
 * ago, or defend one that could never have been captured.
 *
 * The engine only reports what it can prove or read out exhaustively, so these
 * notes can be stated plainly rather than hedged. Note what is deliberately
 * withheld: for a group whose life is still undecided the coach says *that* it
 * is undecided and shows the eye space, but never points at the vital point.
 * Finding that point is the whole exercise; `advise` will give it up if asked.
 */
function lifeNotes(game: Game, color: Color, stage: 1 | 2 | 3): Observation[] {
  const notes: Observation[] = [];
  const size = game.size;
  const position = game.position;
  const mine = colorName(color);
  const theirs = colorName(opponent(color));
  const where = (life: GroupLife) => formatVertex(size, life.stones[0]);

  // One allowance for the whole board, so reading both colours on a crowded
  // 19x19 cannot add up to a stall between the click and the stone appearing.
  const budget = readBudget(position);

  const ourDead: GroupLife[] = [];
  const theirDead: GroupLife[] = [];

  for (const life of lifeAndDeath(position, color, { budget })) {
    if (life.status === 'unsettled') {
      notes.push({
        kind: 'danger',
        headline: `${mine}’s group at ${where(life)} is not yet alive`,
        detail: 'One point in its eye space decides it, and either side can take it.',
        why: 'A group with a single eye space is only alive once that space is split into two eyes. Whoever plays the point that splits it settles the group — which is why finding it is worth more than being told it.',
        highlight: [...life.stones, ...life.eyeSpace],
        concept: 'two-eyes',
        urgency: 95,
      });
      continue;
    }

    if (life.status === 'dead') {
      ourDead.push(life);
      continue;
    }

    if (life.falseEyes.length > 0 && life.status !== 'alive') {
      notes.push({
        kind: 'caution',
        headline: `${formatVertex(size, life.falseEyes[0])} only looks like an eye`,
        detail: `Surrounded by ${mine}, but the stones around it are not joined up.`,
        why: `${theirs} holds the diagonals, so those stones can be split and the point taken. A false eye will not keep a group alive — only count eyes that can be defended.`,
        highlight: [...life.stones, ...life.falseEyes],
        concept: 'eyes',
        urgency: 55,
      });
      continue;
    }

    // Worth saying once while a learner is still guessing at safety, and worth
    // saying with certainty: this is Benson's proof, not an estimate.
    if (life.status === 'alive' && life.certainty === 'proven' && stage === 1) {
      notes.push({
        kind: 'info',
        headline: `${mine}’s group at ${where(life)} is safe for good`,
        detail: 'Two real eyes — it can never be captured.',
        why: `${theirs} would have to fill both eyes, and filling the first is illegal while the second is still there. This group never needs defending again, and adding stones to it only costs points.`,
        highlight: life.stones,
        concept: 'two-eyes',
        urgency: 20,
      });
    }
  }

  // Beginners spend whole games trying to capture groups that are already dead.
  for (const life of lifeAndDeath(position, opponent(color), { budget })) {
    if (life.status === 'dead') theirDead.push(life);
  }

  if (ourDead.length > 0) {
    const space = ourDead.reduce((sum, life) => sum + life.eyeSpace.length, 0);
    notes.push({
      kind: 'danger',
      headline:
        ourDead.length === 1
          ? `${mine}’s group at ${where(ourDead[0])} cannot make two eyes`
          : `${ourDead.length} ${mine} groups cannot make two eyes`,
      detail: `${space} point${space === 1 ? '' : 's'} of eye space in all — not enough room, however it is played.`,
      why: 'A group that cannot be saved is not worth another stone. Every move added here is a wasted one; the profit is elsewhere on the board.',
      highlight: ourDead.flatMap((life) => [...life.stones, ...life.eyeSpace]),
      concept: 'two-eyes',
      urgency: 60,
    });
  }

  if (theirDead.length > 0) {
    notes.push({
      kind: 'opportunity',
      headline:
        theirDead.length === 1
          ? `${theirs}’s group at ${where(theirDead[0])} is already dead`
          : `${theirDead.length} ${theirs} groups are already dead`,
      detail: 'No need to spend moves capturing them.',
      why: `Those stones and the space under them already belong to ${mine}, and they come off the board when the game is scored. Somewhere still undecided is worth more.`,
      highlight: theirDead.flatMap((life) => [...life.stones, ...life.eyeSpace]),
      concept: 'two-eyes',
      urgency: 50,
    });
  }

  return notes;
}
